import React, {useContext, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import './DataCell.scss';
import CategoriesContext from '../CategoriesContext';
import {CLEAR_SEARCH} from '../../utils/ftemConstants';

const DataCell = props => {
    const {
        children,
        isTitle,
        isMiddleRowCell,
        isRightEndCell,
        position,
        colorType,
        columnName,
        isContentCentered,
        isContentBold,
        isMerged,
        isInRange,
        rowTitle,
    } = props;
    const {column, search} = useContext(CategoriesContext);
    const selectedColumn = column[0];
    const searchedTerm = search[0];
    const dataCellRef = useRef(null);
    const isSamePhase = columnName.charAt(0) === selectedColumn?.charAt(0);
    const selectedNum = selectedColumn?.charAt(1);
    const isInRangeF = isInRange?.find(range => range == selectedNum) && isSamePhase;

    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipPos, setTooltipPos] = useState({x: 0, y: 0});
    const tooltipTimeoutRef = useRef(null);
    const tooltipRef = useRef(null);

    const formatRange = range => {
        if (!range || range.length === 0) return '';
        const prefix = columnName.charAt(0);
        if (range.length === 1) return `${prefix}${range[0]}`;
        return `${prefix}${Math.min(...range)}-${prefix}${Math.max(...range)}`;
    };

    const adjustTooltipPosition = event => {
        const offsetX = 10;
        const offsetY = 10;
        let x = event.clientX + offsetX;
        const y = event.clientY + offsetY;

        const viewportWidth = window.innerWidth;
        const edgePadding = 12;
        const edgeThreshold = 250;
        const estimatedTooltipWidth = 200;

        if (tooltipRef.current) {
            const tooltipRect = tooltipRef.current.getBoundingClientRect();

            if (event.clientX > viewportWidth - edgeThreshold) {
                x = viewportWidth - tooltipRect.width - edgePadding;
            } else if (x + tooltipRect.width > viewportWidth - edgePadding) {
                x = event.clientX - tooltipRect.width - offsetX;
            }
        } else if (event.clientX > viewportWidth - edgeThreshold) {
            x = viewportWidth - estimatedTooltipWidth - edgePadding;
        } else if (x + estimatedTooltipWidth > viewportWidth - edgePadding) {
            x = event.clientX - estimatedTooltipWidth - offsetX;
        }

        x = Math.max(0, x);

        setTooltipPos({x, y});
    };

    const handleMouseEnter = event => {
        clearTimeout(tooltipTimeoutRef.current);
        tooltipTimeoutRef.current = setTimeout(() => {
            setShowTooltip(true);
            adjustTooltipPosition(event);
        }, 1000);
    };

    const handleMouseMove = event => {
        if (showTooltip) {
            clearTimeout(tooltipTimeoutRef.current);
            adjustTooltipPosition(event);
            setShowTooltip(false);
        } else {
            clearTimeout(tooltipTimeoutRef.current);
            tooltipTimeoutRef.current = setTimeout(() => {
                setShowTooltip(true);
                adjustTooltipPosition(event);
            }, 1000);
        }
    };

    const handleMouseLeave = () => {
        setShowTooltip(false);
        clearTimeout(tooltipTimeoutRef.current);
    };

    useEffect(() => {
        return () => clearTimeout(tooltipTimeoutRef.current);
    }, []);

    const highlightSearchText = (searchText, container) => {
        if (!container) return;

        function removeExistingHighlights(node) {
            if (!node) return false;
            if (node.nodeType === 1) {
                if (node.className === 'highlight' && node.tagName === 'SPAN') {
                    const parent = node.parentNode;
                    if (parent) {
                        while (node.firstChild) {
                            parent.insertBefore(node.firstChild, node);
                        }
                        parent.removeChild(node);
                        return true;
                    }
                    return false;
                }
                const childNodes = Array.from(node.childNodes);
                let modified = false;
                childNodes.forEach(child => {
                    if (removeExistingHighlights(child)) {
                        modified = true;
                    }
                });
                return modified;
            }
            return false;
        }

        function processNode(node) {
            if (!node) return false;
            if (node.nodeType === 3) {
                const text = node.textContent;
                const regex = new RegExp(`(${searchText})`, 'gi');

                if (searchText && regex.test(text)) {
                    const fragment = document.createDocumentFragment();
                    let lastIndex = 0;
                    let match;

                    regex.lastIndex = 0;

                    while ((match = regex.exec(text)) !== null) {
                        if (match.index > lastIndex) {
                            fragment.appendChild(
                                document.createTextNode(text.slice(lastIndex, match.index)),
                            );
                        }

                        const span = document.createElement('span');
                        span.className = 'highlight';
                        span.textContent = match[0];
                        fragment.appendChild(span);

                        lastIndex = match.index + match[0].length;
                    }

                    if (lastIndex < text.length) {
                        fragment.appendChild(
                            document.createTextNode(text.slice(lastIndex)),
                        );
                    }

                    if (fragment.childNodes.length > 0) {
                        const parent = node.parentNode;
                        if (parent) {
                            parent.replaceChild(fragment, node);
                        }
                    }
                    return true;
                }
            } else if (node.nodeType === 1 && node.nodeName !== 'SPAN') {
                const childNodes = Array.from(node.childNodes);
                let modified = false;
                childNodes.forEach(child => {
                    if (processNode(child)) {
                        modified = true;
                    }
                });
                return modified;
            }
            return false;
        }

        removeExistingHighlights(container);
        container.normalize();

        if (searchText && searchText !== CLEAR_SEARCH) {
            const nodes = Array.from(container.childNodes);
            nodes.forEach(node => processNode(node));
            container.normalize();
        }
    };

    useEffect(() => {
        const currentRef = dataCellRef.current;
        if (!currentRef) return;

        const links = currentRef.querySelectorAll('a');
        links.forEach(link => {
            link.target = '_blank';
        });
    }, []);

    useEffect(() => {
        const currentRef = dataCellRef.current;
        if (!currentRef || !searchedTerm) return;

        const paragraphs = currentRef.querySelectorAll('p');
        if (paragraphs.length === 0) return;

        paragraphs.forEach(paragraph => {
            highlightSearchText(searchedTerm, paragraph);
        });
    }, [searchedTerm]);

    return (
        <div
            ref={dataCellRef}
            className={`sski-c-data-cell sski-c-data-cell--border-radius-${position} ${
                isTitle ? `pt-2 pb-1 sski-c-data-cell--background-${colorType} has-text-white has-text-centered is-size-5 has-text-weight-medium` : ''
            } ${
                isRightEndCell ? 'mr-1' : ''
            } ${
                isMiddleRowCell ? 'sski-c-data-cell--border-bottom' : 'sski-c-data-set__cell-content-merged-bottom'
            } ${
                !isMiddleRowCell && !isTitle ? `sski-c-data-cell--box-shadow-${colorType}` : ''
            } ${
                !isTitle ? 'sski-c-data-cell--min-height' : ''
            } ${
                !isMerged ? 'p-2 pt-4' : ''
            } ${
                isContentCentered ? 'has-text-centered' : ''
            } ${
                isContentBold ? 'has-text-weight-bold is-size-5' : ''
            } ${
                (isInRangeF || selectedColumn === columnName) ? `sski-c-data-cell--background-selected-${colorType}` : ''
            } sski-c-data-cell--background--${colorType}`}
            onMouseEnter={handleMouseEnter}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
        >
            {children}
            {(showTooltip && columnName) && (
                <div
                    ref={tooltipRef}
                    className="sski-c-tooltip  text-sm is-flex rounded is-justify-content-center  is-align-items-center pr-4"
                    style={{
                        left: `${tooltipPos.x}px`,
                        top: `${tooltipPos.y}px`,
                    }}
                >
                    <p className={`sski-c-tooltip__subphase sski-c-tooltip__subphase-${colorType} has-text-white is-flex is-justify-content-center p-2 is-align-items-center mr-2`}>{isInRange.length > 0 ? formatRange(isInRange) : columnName}</p>
                    <p className="sski-c-tooltip__row-title">{rowTitle}</p>
                </div>
            )}
        </div>
    );
};

DataCell.propTypes = {
    isTitle: PropTypes.bool,
    isRightEndCell: PropTypes.bool,
    isMiddleRowCell: PropTypes.bool,
    position: PropTypes.string,
    colorType: PropTypes.string,
    isContentCentered: PropTypes.bool,
    isContentBold: PropTypes.bool,
    columnName: PropTypes.string,
    isMerged: PropTypes.bool,
    isInRange: PropTypes.array,
    subPhaseName: PropTypes.string,
    children: PropTypes.node,
};

DataCell.defaultProps = {
    isTitle: false,
    isRightEndCell: false,
    isMiddleRowCell: false,
    position: '',
    colorType: '',
    isContentCentered: false,
    isContentBold: false,
    columnName: '',
    isMerged: false,
    isInRange: [],
    subPhaseName: '',
};

export default DataCell;
